import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { MdArrowBack, MdCheckCircle, MdChevronRight, MdInfo } from "react-icons/md";
import PersonForm from "../userRegistration/PersonForm";
import LoginCredentialForm from "../userRegistration/LoginCredentialForm";
import RoleSelector from "../userRegistration/RoleSelector";
import OnSuccess from "../commonUtils/OnSuccess";
import { useScope } from "../../../scopes/ScopeProvider";
import { getApiErrorMessage } from "../../../utils/errorHandling";
import { isValidEmail } from "../../../utils/validators";

const StepChip = ({ active, done, children }) => (
  <span className={`badge rounded-pill ${done ? "text-bg-success"
    : active ? "text-bg-primary" : "bg-body border text-body"} border`}>
    {children} {done ? <MdCheckCircle style={{ verticalAlign: "-2px" }} /> : null}
  </span>
);

const normalizeRoles = (values) => {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const normalized = [];
  values.forEach((value) => {
    const role = String(value || "").toUpperCase();
    if (!role || seen.has(role)) return;
    seen.add(role);
    normalized.push(role);
  });
  return normalized;
};

const EmailFirstStep = ({
  defaultRoles,
  onAttached,
  onNewUser,
  onBack,
  submitting,
  attachExistingUser,
}) => {
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState(() => normalizeRoles(defaultRoles).length ? normalizeRoles(defaultRoles) : ["AGENT"]);
  const [showErrors, setShowErrors] = useState(false);

  const trimmedEmail = email.trim();
  const emailInvalid = !isValidEmail(trimmedEmail);
  const rolesInvalid = roles.length === 0;
  const showEmailError = showErrors && emailInvalid;
  const showRoleError = showErrors && rolesInvalid;

  const toggleRole = (value) => {
    const normalized = String(value || "").toUpperCase();
    setRoles((prev) => prev.includes(normalized)
      ? prev.filter((role) => role !== normalized)
      : [...prev, normalized]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (emailInvalid || rolesInvalid || submitting) return;

    if (typeof attachExistingUser !== "function") {
      onNewUser(trimmedEmail, roles);
      return;
    }

    try {
      const attached = await attachExistingUser({ email: trimmedEmail, roles });
      onAttached(attached, roles, trimmedEmail);
    } catch (error) {
      if (error?.response?.status === 404) {
        onNewUser(trimmedEmail, roles);
        return;
      }
      toast.error(getApiErrorMessage(error, "Unable to add this user."));
    }
  };

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="ui-title mb-0">Find User</h3>
          <span className="text-muted small d-flex align-items-center gap-1">
            <MdInfo /> Existing users only need roles
          </span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-3 p-md-4">
        <label className="form-label" htmlFor="add-member-email">Email</label>
        <input
          id="add-member-email"
          type="email"
          className={`form-control ${showEmailError ? "is-invalid" : ""}`}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="agent@example.com"
          autoComplete="email"
          disabled={submitting}
        />
        {showEmailError && <div className="invalid-feedback">Please enter a valid email.</div>}

        <div className="mt-4">
          <RoleSelector
            roles={roles}
            onToggle={toggleRole}
            onSelectAll={() => setRoles(["ADMIN", "AGENT"])}
            onClear={() => setRoles([])}
            disabled={submitting}
            invalid={showRoleError}
            invalidText="Select at least one role."
          />
        </div>

        <div className="ui-actions mt-3 w-100 justify-content-between">
          <button type="button" className="ui-btn" onClick={onBack} disabled={submitting}>
            <MdArrowBack />
            Back
          </button>
          <button
            type="submit"
            className="btn btn-primary rounded-pill d-flex align-items-center gap-2"
            disabled={submitting}
          >
            {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            Continue
          </button>
        </div>
      </form>
    </section>
  );
};

export default function AddUserWizard({
  initialRoles = [],
  onDoneRoute,
  showLabels = true,
}) {
  const scope = useScope();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [knownEmail, setKnownEmail] = useState("");
  const [userData, setUserData] = useState(null);
  const [finalRoles, setFinalRoles] = useState(
    normalizeRoles(Array.isArray(initialRoles) && initialRoles.length
      ? initialRoles
      : (scope.defaultRoles || []))
  );
  const [creating, setCreating] = useState(false);
  const [attachedExisting, setAttachedExisting] = useState(false);

  const createUser = useMutation({
    mutationFn: scope.createUser,
    onSuccess: () => scope.invalidateOnCreate?.(queryClient),
  });

  const attachExisting = useMutation({
    mutationFn: scope.attachExistingUser,
    onSuccess: () => scope.invalidateOnCreate?.(queryClient),
  });

  const handleAddUser = async ({ roles, username, password }) => {
    const rolesToUse = Array.isArray(roles) && roles.length ? roles : finalRoles;
    const emailToUse = knownEmail || username;
    if (!userData) return;

    try {
      setCreating(true);
      await createUser.mutateAsync({
        email: emailToUse,
        password,
        firstName: userData.name,
        lastName: userData.lastName,
        roles: rolesToUse,
        country: userData.country,
        phone: userData.phone,
      });
      setStep(4);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to add a user."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border-0 rounded-4 w-100 add-user-wizard">
      {showLabels && (
        <div className="card-header rounded-top-4 bg-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 p-2">
              <StepChip active={step === 1} done={step > 1}>Email</StepChip>
              <MdChevronRight />
              <StepChip active={step === 2} done={step > 2}>Profile</StepChip>
              <MdChevronRight />
              <StepChip active={step === 3} done={step > 3}>Credentials</StepChip>
              <MdChevronRight />
              <StepChip active={step === 4} done={false}>Done</StepChip>
            </div>
          </div>
        </div>
      )}
      <div className="card-body p-2 py-2">
        {step === 1 && (
          <EmailFirstStep
            defaultRoles={initialRoles.length ? initialRoles : scope.defaultRoles}
            submitting={attachExisting.isPending}
            attachExistingUser={(payload) => attachExisting.mutateAsync(payload)}
            onBack={() => { }}
            onAttached={(user, roles, email) => {
              setKnownEmail(email);
              setFinalRoles(roles);
              setAttachedExisting(true);
              setUserData({ name: user?.firstName || email, lastName: user?.lastName || "" });
              setStep(4);
            }}
            onNewUser={(email, roles) => {
              setKnownEmail(email);
              setFinalRoles(roles);
              setAttachedExisting(false);
              setStep(2);
            }}
          />
        )}

        {step === 2 && (
          <PersonForm
            title="User Info"
            onSubmit={(data) => { setUserData(data); setStep(3); }}
            onBack={() => setStep(1)}
            step={2}
          />
        )}

        {step === 3 && (
          <LoginCredentialForm
            includeEmail={false}
            includeRole={false}
            defaultRoles={initialRoles.length ? initialRoles : scope.defaultRoles}
            onSubmit={async (loginData) => {
              await handleAddUser({ ...loginData, roles: finalRoles, username: knownEmail });
            }}
            onBack={() => setStep(2)}
            submitting={creating}
            submitLabel="Create User"
          />
        )}

        {step === 4 && (
          <OnSuccess
            message={
              <>
                User <span className="text-success">{(userData?.name || knownEmail || "").toUpperCase()}</span>{" "}
                {attachedExisting ? "added successfully" : "created successfully"}
              </>
            }
            details="Click on Done to continue."
            onDone={() => {
              const route = onDoneRoute
                ? onDoneRoute(finalRoles)
                : scope.routeAfterDone?.(finalRoles);
              if (route) scope.navigate?.(route);
            }}
          />
        )}
      </div>
    </div>
  );
}
